/**
 * @file api/logs.js
 * @description API functions for the /api/logs resource.
 */

import apiClient from './client';

/**
 * Fetches a paginated list of request logs with optional filters.
 *
 * @param {Object} [params] - Optional query parameters.
 * @param {number} [params.page=1]      - Page number (1-based).
 * @param {number} [params.limit=50]    - Results per page (max 500).
 * @param {string} [params.path]        - Partial URL path filter.
 * @param {string} [params.remote_ip]   - Partial client IP address filter.
 * @param {string} [params.body]        - Partial match filter on the raw request body text.
 * @param {string} [params.method]      - HTTP method filter.
 * @param {number} [params.status]      - Response status code filter.
 * @param {number} [params.token_id]    - Filter by API token ID.
 * @param {string} [params.from]        - Start of datetime range (ISO 8601).
 * @param {string} [params.to]          - End of datetime range (ISO 8601).
 * @returns {Promise<{ total: number, page: number, limit: number, data: Array }>}
 */
export const listLogs = async (params = {}) => {
  // Strip undefined values so they don't appear as '&key=undefined' in the URL
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
  );
  const res = await apiClient.get('/api/logs', { params: cleanParams });
  return res.data;
};

/**
 * Deletes request logs matching the given filters (admin only).
 * Uses the exact same filter shape as listLogs — an applied filter scopes
 * the deletion to that filtered selection; omitting all filters deletes everything.
 *
 * @param {Object} [filters] - Same filter fields as listLogs (page/limit are ignored).
 * @returns {Promise<{ deleted: number }>}
 */
export const deleteLogs = async (filters = {}) => {
  const cleanParams = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v !== undefined && v !== '')
  );
  const res = await apiClient.delete('/api/logs', { params: cleanParams });
  return res.data;
};

/**
 * Fetches aggregated log metrics grouped by endpoint path + method.
 *
 * @param {Object} [params]
 * @param {string} [params.from] - Start of datetime range (ISO 8601).
 * @param {string} [params.to]   - End of datetime range (ISO 8601).
 * @returns {Promise<Array<{
 *   path: string, method: string,
 *   request_count: number, avg_duration_ms: number,
 *   max_duration_ms: number, total_bytes_in: number, total_bytes_out: number
 * }>>}
 */
export const getMetrics = async (params = {}) => {
  const res = await apiClient.get('/api/logs/metrics', { params });
  return res.data;
};
